'use client';

import { useEffect } from 'react';

/**
 * Keeps our fixed controls clear of AdSense ANCHOR ads.
 *
 * Auto ads dock an anchor ad to the top or bottom edge of the viewport on phones: a fixed
 * `<ins class="adsbygoogle" data-anchor-status="displayed">` at z-index 2147483647, which nothing
 * of ours can outrank. Docked at the bottom it lands exactly where the cookie banner (and its
 * Reject / Accept buttons), the file editors' "Changes" button and toasts sit, so those would be
 * buried; docked at the top it covers the hamburger. Hiding a consent button behind an ad is a
 * compliance problem as well as a usability one.
 *
 * So instead of fighting the ad for z-index, this measures it and publishes how much of each edge
 * it occupies, and those controls move out of its way:
 *   --ad-anchor-bottom  px covered at the bottom edge (0px when no anchor is docked there)
 *   --ad-anchor-top     px covered at the top edge
 * Consumers add the variable to the offset they already compute (`--cookie-banner-h` stacks on
 * top of it: the banner itself sits ON the anchor, everything else sits above both).
 *
 * Measured from the live element, never assumed: the anchor's height differs by device and
 * creative, it animates in, and the user can collapse it with its own toggle. A dismissed or
 * hidden anchor measures 0.
 */
const BOTTOM_VAR = '--ad-anchor-bottom';
const TOP_VAR = '--ad-anchor-top';

/** How close to an edge (px) a fixed anchor must be to count as docked there. */
const EDGE_TOLERANCE = 2;

export function measureAnchorInsets(doc: Document, viewportHeight: number): { top: number; bottom: number } {
  let top = 0;
  let bottom = 0;
  doc.querySelectorAll<HTMLElement>('ins.adsbygoogle[data-anchor-status]').forEach((el) => {
    const status = el.getAttribute('data-anchor-status');
    if (status === 'dismissed') return;
    const style = el.ownerDocument.defaultView?.getComputedStyle(el);
    if (!style || style.position !== 'fixed' || style.display === 'none' || style.visibility === 'hidden') return;
    const r = el.getBoundingClientRect();
    if (r.height <= 0 || r.width <= 0) return;
    if (r.bottom >= viewportHeight - EDGE_TOLERANCE && r.top < viewportHeight) {
      bottom = Math.max(bottom, Math.round(viewportHeight - Math.max(r.top, 0)));
    } else if (r.top <= EDGE_TOLERANCE && r.bottom > 0) {
      top = Math.max(top, Math.round(Math.min(r.bottom, viewportHeight)));
    }
  });
  return { top, bottom };
}

export default function AdAnchorClearance() {
  useEffect(() => {
    const root = document.documentElement;
    let frame = 0;
    let last = '';

    const publish = () => {
      frame = 0;
      const { top, bottom } = measureAnchorInsets(document, window.innerHeight);
      const key = `${top}/${bottom}`;
      if (key === last) return;
      last = key;
      root.style.setProperty(BOTTOM_VAR, `${bottom}px`);
      root.style.setProperty(TOP_VAR, `${top}px`);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(publish);
    };

    // Google appends the anchor to <body> and then resizes / restyles / dismisses it. Watch the
    // body's DIRECT children only (the site itself rewrites style attributes all the time — 3D
    // previews, color pickers — so a subtree attribute watch would fire constantly), and give
    // each anchor found its own attribute + size observers.
    const watched = new WeakSet<Element>();
    const anchorMo = new MutationObserver(schedule);
    const ro = new ResizeObserver(schedule);
    const watchAnchors = () => {
      document.querySelectorAll('ins.adsbygoogle').forEach((el) => {
        if (watched.has(el)) return;
        watched.add(el);
        anchorMo.observe(el, { attributes: true, attributeFilter: ['style', 'data-anchor-status', 'class'] });
        ro.observe(el);
      });
      schedule();
    };
    const bodyMo = new MutationObserver(watchAnchors);
    bodyMo.observe(document.body, { childList: true });
    window.addEventListener('resize', schedule);
    // Its slide-in is a CSS transition: re-measure as it ends.
    document.addEventListener('transitionend', schedule, true);
    watchAnchors();

    return () => {
      bodyMo.disconnect();
      anchorMo.disconnect();
      ro.disconnect();
      window.removeEventListener('resize', schedule);
      document.removeEventListener('transitionend', schedule, true);
      if (frame) cancelAnimationFrame(frame);
      root.style.removeProperty(BOTTOM_VAR);
      root.style.removeProperty(TOP_VAR);
    };
  }, []);

  return null;
}
